import React from 'react';
import { observer } from 'mobx-react';

import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import { IconButton, MenuItem, Popover, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';

import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';

import UploadFileIcon from '@mui/icons-material/UploadFile';

import { Lock } from '@mui/icons-material';

import { colors } from 'theme/colors';

import { humanFileSize } from 'utils/formatters/file';
import { formatMomentDate } from 'utils/formatters/timezone';

import API from 'utils/api';

import { FileActivityResponse } from 'ui/types';

import CustomModal from 'components/CustomModal';

import { Button } from 'components/Button';

import { FileContainer as FileContainerStyled, FileName, FileData, FileIconContainer, Wrapper } from '../styles';

const FileContainer: React.FC<{
  attachment: Omit<FileActivityResponse, 'created'> & { created: string | null };
  deleteFile?: (uuid: string) => void;
  deleteWithoutConfirmation?: boolean;
  disableDownload?: boolean;
  deleteDisabledReason?: string;
}> = ({ attachment, deleteFile, deleteWithoutConfirmation = false, disableDownload = false, deleteDisabledReason }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [openDeleteModal, setOpenDeleteModal] = React.useState<string | null>(null);

  const onCloseModal = (): void => {
    setOpenDeleteModal(null);
  };

  const onDeleteFile = (fileId: string): void => {
    deleteFile?.(fileId);
    setAnchorEl(null);
    setOpenDeleteModal(null);
  };

  const fileCreator = attachment.creator ? `| ${attachment.creator.name} on` : null;
  const fileCreated = formatMomentDate(attachment.created, 'D MMM, YYYY');

  return (
    <>
      <FileContainerStyled key={attachment.uuid}>
        <Wrapper>
          <FileIconContainer>
            <UploadFileIcon sx={{ color: '#757575' }} />
          </FileIconContainer>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <FileName>{attachment.filename}</FileName>
            <FileData>
              {humanFileSize(attachment.size)} {fileCreator}{' '}
              <span data-testid="attachment-created-date">{fileCreated}</span>
            </FileData>
          </Box>
        </Wrapper>
        <Wrapper>
          {!disableDownload && (
            <IconButton
              sx={{
                borderRadius: '50px',
                background: colors.action.hover,
                '&:hover': {
                  background: colors.action.selected,
                },
              }}
              onClick={() => API.downloadFile(attachment.file)()}
            >
              <FileDownloadOutlinedIcon />
            </IconButton>
          )}
          {deleteFile ? (
            <>
              <IconButton
                sx={{
                  borderRadius: '50px',
                  background: colors.action.hover,
                  '&:hover': {
                    background: colors.action.selected,
                  },
                }}
                onClick={(e) => setAnchorEl(e.currentTarget)}
              >
                <MoreVertOutlinedIcon />
              </IconButton>
              <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem
                  id="edit-button"
                  onClick={() =>
                    deleteWithoutConfirmation ? onDeleteFile(attachment.uuid) : setOpenDeleteModal(attachment.uuid)
                  }
                >
                  Delete
                </MenuItem>
              </Popover>
            </>
          ) : (
            <Tooltip title={deleteDisabledReason}>
              {/* span is needed to render tooltip on disabled icon button */}
              <span>
                <IconButton
                  disabled
                  sx={{
                    borderRadius: '50px',
                    background: colors.action.hover,
                    '&.Mui-disabled': {
                      background: colors.action.hover,
                    },
                  }}
                >
                  <Lock htmlColor={colors.grey[400]} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Wrapper>
      </FileContainerStyled>
      <CustomModal
        openModal={!!openDeleteModal}
        onClose={onCloseModal}
        modalTitle="Delete file"
        modalDescription="This file will be permanently removed from the record. Are you sure you want to continue?"
        cancelButton={
          <Button id="cancel-action-button" onClick={onCloseModal} variant="outlined" size="large">
            Cancel
          </Button>
        }
        submitButton={
          <Button
            id="delete-action-button"
            onClick={() => openDeleteModal && onDeleteFile(openDeleteModal)}
            variant="contained"
            size="large"
            color="error"
          >
            Delete
          </Button>
        }
      />
    </>
  );
};

export default observer(FileContainer);
